import * as React from 'react';
import { Security, LoginCallback } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { Navigate } from 'react-router';
import { Route, Routes } from 'react-router-dom';
import { history, persistor, store } from './redux/store';
import { HistoryRouter as Router } from 'redux-first-history/rr6';

import { ClaimDetailsContainer } from './containers/ClaimDetailsContainer';
import { ClaimsContainer } from './containers/ClaimsContainer';
import { DashboardContainer } from './containers/DashboardContainer';
import { ThemeProvider } from '@fluentui/react';
import { FeeScheduleContainer } from './containers/FeeScheduleContainer';
import { Unauthorized } from './components/core/Unauthorized';
import { InsuranceToolChangeHealthcare } from './components/tool/insurance/InsuranceToolChangeHealthcare';
import { InsuranceTool } from './components/tool/insurance/InsuranceTool';
import { MapPage } from './components/map/MapPage';
import { OnSiteManager } from './components/on-site-manager/OnsiteManager';
import { OnSiteManagerPartnerCreateContainer } from './containers/OnSiteManagerPartnerCreateContainer';
import { Page } from './components/core/Page';
import { PatientContainer } from './containers/PatientContainer';
import { PatientsSearchPage } from './components/patient/PatientsSearchPage';
import { PersistGate } from 'redux-persist/integration/react';
import { Provider } from 'react-redux';
import { ProviderContainer } from './containers/Provider';
import { ProviderCreateContainer } from './containers/ProviderCreateContainer';
import { ProvidersContainer } from './containers/ProvidersContainer';
import { ROUTES } from './config/routes';
import { SchedulingContainer } from './containers/SchedulingContainer';
import { SearchVisitsPage } from './components/search/SearchVisitsPage';
import { VisitContainer } from './containers/VisitContainer';
import { VisitsContainer } from './containers/VisitsContainer';
import { MarketMapContainer } from './containers/MarketMapContainer';
import { ConciergeContainer } from './components/concierge/Concierge';
import { AccountContainer } from './containers/AccountContainer';
import { AgencyContainer } from './components/concierge/Agency';
import { PlansContainer } from './containers/PlansContainer';
import { InsuranceRulesContainer } from './containers/InsuranceRulesContainer';
import { VisitTimeline } from './components/visit/VisitTimeline';
import { ScheduleOptimizerTool } from './components/tool/ScheduleOptimizerTool';
import { Webhooks } from './components/webhooks/Webhooks';
import { WebhookSubscriber } from './components/webhooks/WebhookSubscriber';
import { Markets } from './components/markets/Markets';
import { MarketDetails } from './components/markets/MarketDetails';
import { AdminTool } from './components/tool/AdminTool';
import { ProviderVisits } from './components/visit/ProviderVisits';
import { useJsApiLoader } from '@react-google-maps/api';
import { FeatureFlagProvider } from './components/feature-flag/FeatureFlagProvider';
import { InsuranceToolTester } from './components/tool/insurance/InsuranceToolTester';
import { CreateVisit } from './components/create-visit/CreateVisit';
import { oktaAuth } from './okta';
import PodRouter from './components/pod/state/Router';

declare const healConfig: any;

const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
  history.replace(toRelativeUrl(originalUri, window.location.origin));
};

const AppRoutes = () => (
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <FeatureFlagProvider>
        <ThemeProvider>
          <Router history={history}>
            <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
              <Routes>
                <Route path="/implicit/callback" element={<LoginCallback errorComponent={(e) => <Unauthorized error={e?.error} />} />} />
                <Route path="/unauthorized" element={<Unauthorized />} />
                <Route element={<Page />}>
                  <Route path={ROUTES.home} element={<VisitsContainer />} />
                  <Route path={ROUTES.dashboard} element={<DashboardContainer />} />
                  <Route path={ROUTES.visitsMap} element={<MapPage />} />
                  <Route path={ROUTES.visitsTimeline} element={<VisitTimeline />} />
                  <Route path={ROUTES.visitsProvider} element={<ProviderVisits />} />
                  <Route path={ROUTES.visits} element={<VisitsContainer />} />
                  <Route path={ROUTES.visits + '/:code'} element={<VisitContainer />} />
                  <Route path={ROUTES.visits + '/:code/referral/:zendeskTicketId'} element={<VisitContainer />} />
                  <Route path={ROUTES.createVisit} element={<CreateVisit />} />
                  <Route path={ROUTES.markets} element={<Markets />} />
                  <Route path={ROUTES.markets + '/:code'} element={<MarketDetails />} />
                  <Route path={ROUTES.patients} element={<PatientsSearchPage />} />
                  <Route path={ROUTES.patients + '/:id'} element={<Navigate to={'profile'} replace />} />
                  <Route path={ROUTES.patients + '/:id/:tab'} element={<PatientContainer />} />
                  <Route path={ROUTES.toolAdmin} element={<AdminTool />} />
                  <Route path={ROUTES.concierge} element={<ConciergeContainer />} />
                  <Route path={ROUTES.agency + '/:id'} element={<Navigate to={'agents'} replace />} />
                  <Route path={ROUTES.agency + '/:id/:tab'} element={<AgencyContainer />} />
                  <Route path={ROUTES.accounts + '/:id'} element={<Navigate to={'profile'} replace />} />
                  <Route path={ROUTES.accounts + '/:id/:tab'} element={<AccountContainer />} />
                  <Route path={ROUTES.providers} element={<ProvidersContainer />} />
                  <Route path={ROUTES.providerCreate} element={<ProviderCreateContainer />} />
                  <Route path={ROUTES.providers + '/:id'} element={<Navigate to={'profile'} replace />} />
                  <Route path={ROUTES.providers + '/:id/:tab'} element={<ProviderContainer />} />
                  <Route path={ROUTES.pods + '/*'} element={<PodRouter />} />
                  <Route path={ROUTES.claims} element={<ClaimsContainer />} />
                  <Route path={ROUTES.claims + '/:id'} element={<ClaimDetailsContainer />} />
                  <Route path={ROUTES.feeSchedule} element={<FeeScheduleContainer />} />
                  <Route path={ROUTES.onSiteManagerCreatePartner} element={<OnSiteManagerPartnerCreateContainer />} />
                  <Route path={ROUTES.onSiteManagerEditPartner} element={<OnSiteManagerPartnerCreateContainer />} />
                  <Route path={ROUTES.onSiteManager} element={<OnSiteManager />} />
                  <Route path={ROUTES.search} element={<SearchVisitsPage />} />
                  <Route path={ROUTES.toolInsurance} element={<InsuranceTool />} />
                  <Route path={ROUTES.toolInsurance + '2'} element={<InsuranceToolChangeHealthcare />} />
                  <Route path={ROUTES.toolInsuranceTester} element={<InsuranceToolTester />} />
                  <Route path={ROUTES.insuranceRules} element={<InsuranceRulesContainer />} />
                  <Route path={ROUTES.toolOptimize} element={<ScheduleOptimizerTool />} />
                  <Route path={ROUTES.scheduling} element={<SchedulingContainer />} />
                  <Route path={ROUTES.marketMap} element={<MarketMapContainer />} />
                  <Route path={ROUTES.plans} element={<PlansContainer />} />
                  <Route path={ROUTES.webhooks} element={<Webhooks />} />
                  <Route path={ROUTES.webhooks + '/:subscriberId'} element={<WebhookSubscriber />} />
                </Route>
              </Routes>
            </Security>
          </Router>
        </ThemeProvider>
      </FeatureFlagProvider>
    </PersistGate>
  </Provider>
);

const libraries: any = ['visualization', 'geometry', 'places'];
const WrappedApp = () => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: healConfig.googleMapsApiKey,
    libraries,
  });
  if (!isLoaded) {
    return null;
  }
  return <AppRoutes />;
};

export default <WrappedApp />;
